(() => {
  if (customElements.get('bestseller-carousel')) return;

  function debounce(fn, wait) {
    let t;
    return function debounced() {
      const ctx = this;
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  class BestsellerCarousel extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;

      this.layout = this.dataset.layout || 'carousel';
      this.viewport = this.querySelector('[data-bsc-viewport]');
      this.track = this.querySelector('[data-bsc-track]');
      this.prevBtn = this.querySelector('[data-bsc-prev]');
      this.nextBtn = this.querySelector('[data-bsc-next]');

      if (!this.viewport || !this.track) return;

      this._initialized = true;
      this.originalSlides = Array.from(this.track.children);
      this.dragEnabled = this.dataset.drag === 'true';
      this.infinite = this.dataset.infinite === 'true';
      this.cloneCount = 0;

      if (this.layout === 'grid') {
        return;
      }

      this._onScroll = this._handleScrollRAF.bind(this);
      this.viewport.addEventListener('scroll', this._onScroll);

      this._debouncedRecenter = debounce(() => {
        this.recenter(true);
      }, 150);
      window.addEventListener('resize', this._debouncedRecenter);

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this._onSectionLoad = (e) => {
          if (!e || !e.target || typeof e.target.contains !== 'function') return;
          if (!e.target.contains(this)) return;
          requestAnimationFrame(() => {
            this.recenter(true);
            this.updateArrowState();
          });
        };
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }

      if (this.infinite && this.originalSlides.length >= 2) {
        this.setupInfiniteClones();
      }

      this.bindArrows();
      if (this.dragEnabled) this.bindDrag();

      requestAnimationFrame(() => {
        this.recenter(true);
        this.updateArrowState();
      });
    }

    disconnectedCallback() {
      this._initialized = false;
      if (this.viewport && this._onScroll) {
        this.viewport.removeEventListener('scroll', this._onScroll);
      }
      if (this._debouncedRecenter) {
        window.removeEventListener('resize', this._debouncedRecenter);
      }
      if (this._onSectionLoad) {
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
      if (this._cleanupDrag) this._cleanupDrag();
      if (this._onPrev) {
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      }
      if (this._scrollFrame) {
        cancelAnimationFrame(this._scrollFrame);
      }
    }

    setupInfiniteClones() {
      const slides = this.originalSlides;
      if (!slides.length || slides.length < 2) return;

      this.cloneCount = Math.min(slides.length, 6);

      const prepClone = (node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('*').forEach((el) => {
          if (el.shadowRoot) return;
          [...el.attributes].forEach((attr) => {
            if (attr.name.startsWith('data-shopify')) el.removeAttribute(attr.name);
          });
        });
        return clone;
      };

      for (let i = slides.length - this.cloneCount; i < slides.length; i++) {
        this.track.insertBefore(prepClone(slides[i]), this.track.firstChild);
      }
      for (let i = 0; i < this.cloneCount; i++) {
        this.track.appendChild(prepClone(slides[i]));
      }
    }

    recenter(instant) {
      if (!this.infinite || !this.cloneCount) return;
      const firstOriginal = this.track.children[this.cloneCount];
      if (!firstOriginal) return;
      const behavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = firstOriginal.offsetLeft - this.getPeek();
      if (!instant) {
        void this.viewport.offsetWidth;
      }
      this.viewport.style.scrollBehavior = behavior;
    }

    getPeek() {
      const cs = getComputedStyle(this.viewport);
      return parseFloat(cs.paddingLeft) || 0;
    }

    getSlideWidth() {
      const slide = this.track.children[0];
      if (!slide) return 0;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    bindArrows() {
      this._onPrev = () => this.scrollByCol(-1);
      this._onNext = () => this.scrollByCol(1);
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
    }

    scrollByCol(direction) {
      const slideW = this.getSlideWidth();
      if (!slideW) return;
      this.viewport.scrollBy({
        left: slideW * direction,
        behavior: 'smooth',
      });
    }

    _handleScrollRAF() {
      if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame);
      this._scrollFrame = requestAnimationFrame(() => {
        this.handleScroll();
        this._scrollFrame = null;
      });
    }

    handleScroll() {
      if (this.infinite && this.cloneCount) {
        const slideW = this.getSlideWidth();
        if (!slideW) return;
        const scrollLeft = this.viewport.scrollLeft;
        const originalLen = this.originalSlides.length;

        const firstReal = this.track.children[this.cloneCount];
        const lastReal = this.track.children[this.cloneCount + originalLen - 1];
        if (!firstReal || !lastReal) return;

        const firstRealLeft = firstReal.offsetLeft - this.getPeek();
        const lastRealLeft = lastReal.offsetLeft - this.getPeek();

        if (scrollLeft < firstRealLeft - slideW * 0.5) {
          const delta = firstRealLeft - scrollLeft;
          this.jumpTo(lastRealLeft + slideW - delta);
        } else if (scrollLeft > lastRealLeft + slideW * 0.5) {
          const delta = scrollLeft - (lastRealLeft + slideW);
          this.jumpTo(firstRealLeft + delta);
        }
      } else {
        this.updateArrowState();
      }
    }

    jumpTo(position) {
      const behavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = position;
      this.viewport.style.scrollBehavior = behavior;
    }

    updateArrowState() {
      if (this.infinite) return;
      if (!this.prevBtn || !this.nextBtn) return;
      const sl = this.viewport.scrollLeft;
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      this.prevBtn.disabled = sl <= 1;
      this.nextBtn.disabled = sl >= max - 1;
    }

    bindDrag() {
      const DRAG_THRESHOLD = 8;

      let pointerDown = false;
      let isDragging = false;
      let startX = 0;
      let startScroll = 0;
      let pointerId = null;

      const onDown = (e) => {
        if (e.pointerType !== 'mouse') return;
        if (e.button !== 0) return;
        pointerDown = true;
        isDragging = false;
        startX = e.clientX;
        startScroll = this.viewport.scrollLeft;
        pointerId = e.pointerId;
      };

      const onMove = (e) => {
        if (!pointerDown) return;
        const dx = e.clientX - startX;

        if (!isDragging) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;
          isDragging = true;
          this.viewport.classList.add('is-dragging');
          try {
            this.viewport.setPointerCapture(pointerId);
          } catch (_) {}
        }

        this.viewport.scrollLeft = startScroll - dx;
        e.preventDefault();
      };

      const onUp = () => {
        if (!pointerDown) return;
        const wasDragging = isDragging;
        pointerDown = false;
        isDragging = false;
        this.viewport.classList.remove('is-dragging');
        if (pointerId != null) {
          try {
            this.viewport.releasePointerCapture(pointerId);
          } catch (_) {}
          pointerId = null;
        }
        if (wasDragging) {
          this.viewport.classList.add('was-dragging');
          setTimeout(() => this.viewport.classList.remove('was-dragging'), 60);
          this.snapToNearest();
        }
      };

      const onClickCapture = (e) => {
        if (this.viewport.classList.contains('was-dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      const onDragStart = (e) => e.preventDefault();

      this.viewport.addEventListener('pointerdown', onDown);
      this.viewport.addEventListener('pointermove', onMove);
      this.viewport.addEventListener('pointerup', onUp);
      this.viewport.addEventListener('pointercancel', onUp);
      this.viewport.addEventListener('click', onClickCapture, true);
      this.viewport.addEventListener('dragstart', onDragStart);

      this._cleanupDrag = () => {
        this.viewport.removeEventListener('pointerdown', onDown);
        this.viewport.removeEventListener('pointermove', onMove);
        this.viewport.removeEventListener('pointerup', onUp);
        this.viewport.removeEventListener('pointercancel', onUp);
        this.viewport.removeEventListener('click', onClickCapture, true);
        this.viewport.removeEventListener('dragstart', onDragStart);
      };
    }

    snapToNearest() {
      const slideW = this.getSlideWidth();
      if (!slideW) return;
      const peek = this.getPeek();
      const sl = this.viewport.scrollLeft;
      const idx = Math.round((sl + peek) / slideW);
      const target = idx * slideW - peek;
      this.viewport.scrollTo({ left: target, behavior: 'smooth' });
    }
  }

  customElements.define('bestseller-carousel', BestsellerCarousel);
})();
